import React, { useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { InfoCircledIcon } from '@radix-ui/react-icons'
import { Key, Copy, CheckCircle, Loader2, Trash2 } from 'lucide-react'
import {
  useCreateStorageVolume,
  useCreateGoogleDriveToken,
  useAddGoogleDrive,
  useListGoogleDrives,
  useDeleteGoogleDrive,
  useGoogleDriveTokenCheck,
  useDeleteGoogleDriveToken,
  getTokenMessage,
} from '@/services/storage'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'
import { useUserStore } from '@/store/UserStore'
import { GoogleDrive } from '@/services/inference.gen'
import { useActiveOrgProfile } from '@/hooks/useActiveOrgProfile'
import { Spinner } from '@/components/spinner'
import { StorageTypeId } from '@/constants/storageTypes'

type CreateGoogleDriveStorageProps = {
  onSuccess: () => void
}

export const CreateGoogleDriveStorage = ({
  onSuccess,
}: CreateGoogleDriveStorageProps) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [tokenCopied, setTokenCopied] = useState(false)
  const [showFolderSetupHint, setShowFolderSetupHint] = useState(false)
  const [urlEntered, setUrlEntered] = useState(false)
  const [driveExistsError, setDriveExistsError] = useState(false)
  const [existingDrive, setExistingDrive] = useState<GoogleDrive | null>(null)
  const [showTokenRevalidation, setShowTokenRevalidation] = useState(false)
  const [emailSame, setEmailSame] = useState(true)
  const [customEmail, setCustomEmail] = useState('')
  const [usingExistingDrive, setUsingExistingDrive] = useState(false)

  const { user } = useUserStore()

  const {
    data: activeOrgProfile,
    isLoading: isLoadingActiveOrgProfile,
    isError: isErrorActiveOrgProfile,
  } = useActiveOrgProfile()

  const storageSizeLimit =
    activeOrgProfile?.org?.metadata?.limits?.org_per_user_storage_gb
  const storageCountLimit =
    activeOrgProfile?.org?.metadata?.limits?.org_per_user_storage_count

  const FormSchema = useMemo(
    () =>
      z.object({
        name: z.string().min(1, 'Name is required'),
        size_gb: z
          .number()
          .min(1, 'Size must be at least 1GB')
          .max(
            storageSizeLimit || 0,
            `Size cannot exceed ${storageSizeLimit}GB`
          ),
        is_public: z.boolean().default(false),
        google_drive_url: z.string().min(1, 'Google Drive URL is required'),
      }),
    [storageSizeLimit]
  )

  type FormValues = z.infer<typeof FormSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: '',
      size_gb: storageSizeLimit,
      is_public: false,
      google_drive_url: '',
    },
  })

  const { mutateAsync: createStorageVolumeMutate } = useCreateStorageVolume()
  const {
    mutateAsync: createDriveToken,
    data: driveToken,
    isPending: isCreatingDriveToken,
    isError: isCreatingDriveTokenError,
  } = useCreateGoogleDriveToken()
  const { mutateAsync: addGoogleDrive } = useAddGoogleDrive()
  const { data: googleDrives } = useListGoogleDrives()
  const { mutateAsync: deleteDrive, isPending: isDeleting } =
    useDeleteGoogleDrive()
  const { mutateAsync: deleteToken } = useDeleteGoogleDriveToken()

  const driveUrl = form.watch('google_drive_url')
  const checkToken = useGoogleDriveTokenCheck()

  // Normalize Google Drive URLs to shareable format
  const normalizeDriveUrl = (url: string) =>
    url.replace(/\/drive\/u\/\d+\/folders/, '/drive/folders').split('?')[0]

  // Reset states when form resets
  const resetStates = () => {
    setTokenCopied(false)
    setCurrentStep(2)
    setShowFolderSetupHint(false)
    setUrlEntered(false)
    setDriveExistsError(false)
    setExistingDrive(null)
    setShowTokenRevalidation(false)
    setEmailSame(true)
    setCustomEmail('')
    setUsingExistingDrive(false)
  }

  // Update steps based on form state
  useEffect(() => {
    if (driveToken && !showFolderSetupHint) {
      setCurrentStep(2)
    }
  }, [driveToken, showFolderSetupHint])

  // Reset states when drive URL changes
  useEffect(() => {
    setDriveExistsError(false)
    setExistingDrive(null)
    setShowTokenRevalidation(false)
    setUsingExistingDrive(false)
    setUrlEntered(false)
  }, [driveUrl])

  const handleCopyToken = () => {
    const token = driveToken
    if (token) {
      navigator.clipboard.writeText(getTokenMessage(token))
      toast.success('Token copied to clipboard')
      setTokenCopied(true)

      // After copying, show the next step hint
      setTimeout(() => {
        setShowFolderSetupHint(true)
        setCurrentStep(3)
      }, 1000)
    }
  }

  const onSubmit = async (data: FormValues) => {
    try {
      const gdMetadata = []

      if (existingDrive) {
        // Use existing Google Drive
        gdMetadata.push({
          google_drive_id: existingDrive.id,
          file_path: '',
        })
      } else {
        const emailToUse = emailSame ? user?.email : customEmail
        if (!driveToken || !emailToUse || !data.google_drive_url) {
          toast.error('Google Drive token, email, and URL are required')
          return
        }

        const googleDriveResult = await addGoogleDrive({
          url: normalizeDriveUrl(data.google_drive_url),
          token: driveToken,
          email: emailToUse,
        })

        gdMetadata.push({
          google_drive_id: googleDriveResult.id,
          file_path: '',
        })
      }

      await createStorageVolumeMutate({
        name: data.name,
        size_gb: data.size_gb,
        read_only: false,
        is_public: data.is_public,
        google_drive_metadata: gdMetadata,
        type_id: StorageTypeId.S3,
      })

      form.reset()
      resetStates()
      onSuccess()
      toast.success('Storage volume created successfully')
    } catch (error) {
      console.error(error)
      toast.error('Failed to create storage volume')
    }
  }

  const handleDeleteDrive = async () => {
    if (!existingDrive?.id) {
      toast.error('No drive to delete')
      return
    }

    try {
      await deleteDrive({ id: existingDrive.id })
      // remove any existing token for this URL
      const tokenCheck = await checkToken.mutateAsync(
        normalizeDriveUrl(existingDrive.url)
      )
      if (tokenCheck.existingToken) {
        await deleteToken(tokenCheck.existingToken.id)
      }
      toast.success('Google Drive connection deleted')
      setDriveExistsError(false)
      setExistingDrive(null)

      createDriveToken(driveUrl)
    } catch (error) {
      console.error(error)
      toast.error('Failed to delete Google Drive connection')
    }
  }

  const handleGetToken = async () => {
    if (!driveUrl) {
      toast.error('Please enter a Google Drive URL first')
      return
    }

    setDriveExistsError(false)
    setShowTokenRevalidation(false)
    const normalizedUrl = normalizeDriveUrl(driveUrl)

    try {
      await createDriveToken(normalizedUrl)
      setUrlEntered(true)
    } catch (error: unknown) {
      // First check if there are existing Google Drives with this URL
      const matchingDrive = googleDrives?.items.find(
        (drive) => drive.url.trim() === normalizedUrl.trim()
      )

      if (matchingDrive) {
        setDriveExistsError(true)
        setExistingDrive(matchingDrive)
        setUrlEntered(true)
      } else {
        // No matching drives found, check if there are existing tokens
        const tokenCheck = await checkToken.mutateAsync(normalizedUrl)

        if (tokenCheck.hasExistingToken) {
          // Show revalidation option if existing token found
          setShowTokenRevalidation(true)
          setUrlEntered(true)
          toast.error(
            'Token generation failed. A token already exists for this URL. Please revalidate the token.'
          )
        } else {
          console.error('Error generating token:', error)
          toast.error(
            'Failed to generate token: ' +
              (error instanceof Error ? error.message : 'Unknown error')
          )
        }
      }
    }
  }

  if (isLoadingActiveOrgProfile) {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <Spinner />
      </div>
    )
  }

  if (isErrorActiveOrgProfile) {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <p className="text-sm text-destructive-foreground">
          Failed to fetch organization profile. Please contact support.
        </p>
      </div>
    )
  }

  if (!storageSizeLimit || !storageCountLimit) {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <p className="text-sm text-destructive-foreground">
          No storage limits found. Please contact support.
        </p>
      </div>
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-5"
        id="google-drive-storage-form"
      >
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <InfoCircledIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-primary">Connect Google Drive</h4>
              <ol className="mt-1 text-sm text-primary/80 list-decimal pl-5 space-y-1.5">
                <li>Enter your Google Drive folder URL below</li>
                <li>
                  Share the folder with{' '}
                  <code className="px-1.5 py-0.5 bg-primary/10 border border-primary/20 rounded text-xs font-mono">
                    drive-access@inference.ai
                  </code>
                </li>
                <li>Include the token below in your sharing message</li>
                {showFolderSetupHint && (
                  <li className="text-primary font-medium">
                    After sharing, click 'Create Storage' to finish setup
                  </li>
                )}
              </ol>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="my-storage" {...field} />
                </FormControl>
                <FormDescription className="text-xs">
                  A unique name for your storage
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="size_gb"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Size (GB)</FormLabel>
                  <Badge variant="outline" className="text-xs font-normal">
                    Max: {storageSizeLimit}GB
                  </Badge>
                </div>
                <FormControl>
                  <Input
                    type="number"
                    placeholder={storageSizeLimit.toString()}
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <div className="mt-1.5">
                  <Progress
                    value={(form.watch('size_gb') / storageSizeLimit) * 100}
                    className="h-1.5"
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4">
          <FormField
            control={form.control}
            name="google_drive_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Google Drive Folder URL</FormLabel>
                <div className="flex space-x-2">
                  <FormControl>
                    <Input
                      placeholder="http://drive.google.com/drive/*"
                      {...field}
                    />
                  </FormControl>
                  <Button
                    type="button"
                    onClick={handleGetToken}
                    disabled={isCreatingDriveToken || !driveUrl}
                    className="shrink-0"
                  >
                    {isCreatingDriveToken ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Key className="h-4 w-4 mr-1" />
                    )}
                    Get Token
                  </Button>
                </div>
                <FormDescription className="text-xs">
                  Paste the URL of the Google Drive folder you want to share,
                  then click "Get Token"
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Token revalidation message */}
          {showTokenRevalidation && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                Token generation failed. A token already exists for this URL.
                You may need to revalidate the existing token.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const tokenCheck = await checkToken.mutateAsync(driveUrl)
                    if (tokenCheck.existingToken) {
                      // Delete old token and create new one
                      await deleteToken(tokenCheck.existingToken.id)
                      await createDriveToken(driveUrl)
                      setShowTokenRevalidation(false)
                      setUrlEntered(true)
                      toast.success('Token revalidated successfully')
                    }
                  } catch (error) {
                    toast.error('Failed to revalidate token')
                  }
                }}
                disabled={checkToken.isPending}
              >
                {checkToken.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Key className="h-4 w-4 mr-1" />
                )}
                Revalidate Token
              </Button>
            </div>
          )}
        </div>

        {/* Only show token after URL is entered and not using existing drive */}
        {urlEntered && !showTokenRevalidation && !usingExistingDrive && (
          <div
            className={cn(
              'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 animate-in fade-in duration-300',
              driveExistsError &&
                'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-800'
            )}
          >
            <div className="flex items-start gap-3">
              <Key
                className={cn(
                  'w-5 h-5 mt-0.5 flex-shrink-0',
                  driveExistsError
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-amber-600 dark:text-amber-400'
                )}
              />
              <div className="flex-1">
                {driveExistsError ? (
                  <>
                    <h4 className="font-medium text-orange-800 dark:text-orange-300">
                      Previously Connected Drive
                    </h4>
                    <p className="text-sm text-orange-700 dark:text-orange-400 mt-1 mb-3">
                      {existingDrive?.storage_volumes &&
                      existingDrive?.storage_volumes?.length > 0 ? (
                        <>
                          This Google Drive URL is already connected to{' '}
                          {existingDrive?.storage_volumes?.length || 0} storage
                          volume
                          {existingDrive?.storage_volumes?.length !== 1
                            ? 's'
                            : ''}
                          . You can attach this existing drive to your new
                          storage volume.
                        </>
                      ) : (
                        <>
                          This Google Drive was previously connected to a
                          storage volume, but it has been deleted. You can
                          safely delete this connection and generate a new
                          token.
                        </>
                      )}
                    </p>
                    {existingDrive?.storage_volumes &&
                    existingDrive.storage_volumes.length > 0 ? (
                      <>
                        <ul className="text-xs bg-white dark:bg-gray-800 border border-orange-200 dark:border-orange-800 rounded-md p-2 mb-3 space-y-1">
                          {existingDrive.storage_volumes.map((vol) => (
                            <li key={vol.volume_id} className="font-medium">
                              - "{vol.volume_name}", size: {vol.size_gb} GB
                            </li>
                          ))}
                        </ul>
                        <div className="space-y-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => {
                              setUsingExistingDrive(true)
                              setDriveExistsError(false)
                              toast.success(
                                'Ready to create storage with existing Google Drive'
                              )
                            }}
                          >
                            Attach existing drive to new storage volume
                          </Button>
                          {/* <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="w-full"
                            onClick={handleDeleteDrive}
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <span className="flex items-center gap-1.5">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Deleting...
                              </span>
                            ) : (
                              <span className="flex items-center gap-1.5">
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete & Generate New Token
                              </span>
                            )}
                          </Button> */}
                        </div>
                      </>
                    ) : (
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="w-full"
                        onClick={handleDeleteDrive}
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <span className="flex items-center gap-1.5">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Deleting...
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5">
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete the drive & Generate New Token
                          </span>
                        )}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <h4 className="font-medium text-amber-600 dark:text-amber-400">
                      Access Token
                    </h4>
                    <div className="relative mt-2">
                      <div
                        className={cn(
                          'flex justify-between items-center bg-amber-100 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 rounded font-mono text-sm',
                          isCreatingDriveToken && 'animate-pulse',
                          tokenCopied &&
                            'border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                        )}
                      >
                        <span className="pr-8 break-all">
                          {getTokenMessage(driveToken) ??
                            (isCreatingDriveToken
                              ? 'Generating token...'
                              : isCreatingDriveTokenError
                                ? 'Error generating token'
                                : '')}
                        </span>
                        {driveToken && (
                          <button
                            type="button"
                            className={cn(
                              'absolute right-2 top-1/2 transform -translate-y-1/2',
                              tokenCopied
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-500'
                            )}
                            onClick={handleCopyToken}
                          >
                            {tokenCopied ? (
                              <CheckCircle className="h-4 w-4" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 flex items-center">
                      {tokenCopied ? (
                        <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Token copied! Now share your Drive folder
                        </span>
                      ) : (
                        'Click to copy this token and include it in your sharing message'
                      )}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Show success section when using existing drive */}
        {usingExistingDrive && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 animate-in fade-in duration-300">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-primary">
                  Using Existing Google Drive Connection
                </h4>
                <p className="text-sm text-primary/80 mt-1">
                  Your storage volume will be connected to the existing Google
                  Drive. No additional token setup required.
                </p>
              </div>
            </div>
          </div>
        )}

        {showFolderSetupHint && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 animate-in fade-in duration-300">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-primary">
                  Last Step: Share Drive to drive-access@inference.ai & Include
                  Token
                </h4>
                <p className="text-sm text-primary/80 mt-1">
                  Now share the drive url's data with{' '}
                  <b>drive-access@inference.ai</b> and{' '}
                  <b>include the token below</b> in your sharing message.
                </p>
              </div>
            </div>
          </div>
        )}

        {showFolderSetupHint && (
          <div className="mt-4 space-y-2">
            <span>
              Is the folder shared with your signed-in email (
              <b>{user?.email || ''}</b>)?
            </span>
            <div className="flex space-x-2 mt-1">
              <Button
                type="button"
                size="sm"
                variant={emailSame ? 'default' : 'outline'}
                onClick={() => setEmailSame(true)}
              >
                Yes
              </Button>
              <Button
                type="button"
                size="sm"
                variant={!emailSame ? 'destructive' : 'outline'}
                onClick={() => setEmailSame(false)}
              >
                No
              </Button>
            </div>
            {!emailSame && (
              <Input
                placeholder="Enter the email you used to share the folder"
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                className="mt-2"
              />
            )}
          </div>
        )}

        <FormField
          control={form.control}
          name="is_public"
          render={({ field }) => (
            <FormItem
              className={cn(
                'flex flex-row items-center justify-between rounded-lg border p-3',
                field.value && 'bg-primary/10 border-primary/20'
              )}
            >
              <div className="space-y-0.5">
                <FormLabel>Organization-wide access</FormLabel>
                <FormDescription className="text-xs">
                  Make storage readable by all users in your organization
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="bg-muted ring-ring ring rounded-md p-3 text-sm flex items-start gap-2">
          <InfoCircledIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium">Organization Limits:</span>{' '}
            {storageCountLimit} active storage volumes, max {storageSizeLimit}GB
            per volume
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            disabled={isCreatingDriveToken}
            type="submit"
            className={cn(
              showFolderSetupHint &&
                'animate-pulse bg-primary hover:bg-primary/90'
            )}
          >
            {isCreatingDriveToken ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Creating...
              </span>
            ) : (
              'Create Storage'
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
